// Load polyfills (Object.groupBy, Array.prototype.at, String.prototype.replaceAll) before the route tree
// evaluates, since router.tsx is the client entry and routeTree.gen pulls in every route module.
import './polyfills'
import type { ReactNode } from 'react'
import { isServer } from '@tanstack/react-query'
import { createRouter } from '@tanstack/react-router'
import { setupRouterSsrQueryIntegration } from '@tanstack/react-router-ssr-query'
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client'
import { routeTree } from './routeTree.gen'
import { makeQueryClient, queryClient as clientQueryClient, persistOptions } from './main'
import { useAppStore } from './lib/app-store'
import { createLocaleI18n, loadTranslation } from './lib/locales'
import { resolveServerLocale } from './lib/server-locale'
import type { Locale } from 'shared/locales'
import { setupAnalytics } from './lib/analytics'
import { DefaultRouteError } from './route-error'
import { logFrontendError } from './lib/log-error'
import { addBreadcrumb, installBreadcrumbCapture } from './lib/breadcrumbs'
import { installClientActionCapture, trackNavigation } from './lib/session-actions'
import { installVersionCapture, suppressTanstackChunkReload } from './lib/app-version'

/**
 * Router factory consumed by TanStack Start. The app is full-SSR: loaders run on the server and deferred
 * prefetches stream their result. That is load-bearing for the report routes — the server-rendered HTML is what
 * paints the report on reload (the persisted-cache restore is async and racy), so they must NOT be
 * `ssr: 'data-only'`. The query client must be per-request on the server; a module singleton would leak one
 * request's dehydrated cache into the next on the long-lived server.
 */
export async function getRouter() {
  const queryClient = isServer ? makeQueryClient() : clientQueryClient

  // Resolve the request's locale ONCE, here, so SSR and the first client render agree — and load +
  // activate its catalog BEFORE the router is created and React renders/hydrates.
  const locale: Locale = isServer ? (resolveServerLocale() ?? 'es-ES') : useAppStore.getState().store.locale

  // Request-scoped Lingui instance: on the server this isolates each request's active locale from concurrent
  // renders sharing the worker (React #418 hydration mismatches). See createLocaleI18n.
  const i18nInstance = createLocaleI18n()
  await loadTranslation(locale, i18nInstance)

  const router = createRouter({
    routeTree,
    // Loaders prefetch into this client via `context.queryClient`; the ssr-query
    // integration below dehydrates the same instance and streams it to the client.
    context: { queryClient, locale, i18n: i18nInstance },
    defaultPreload: 'intent',
    // Loader calls must never be stale, so the loader runs whenever a route is preloaded or visited.
    defaultPreloadStaleTime: 0,
    // Every route with a component declares its own skeleton (scripts/check-route-pending.mjs), shown on the click
    // frame: the router awaits a route's chunk before rendering anything, and at the library defaults (1000/500) a
    // cold navigation left the previous page on screen for the whole download. No defaultPendingComponent, on
    // purpose: a component-less data layout must have NOTHING to present, or its search re-key would blank its
    // subtree at 0 (../scripts/check-route-pending.md).
    defaultPendingMs: 0,
    defaultPendingMinMs: 0,
    // A hover preload caches the destination as a `success` match the instant its loader resolves — every loader
    // here is fire-and-forget — while its chunk is still downloading. The click that follows inside that window
    // adopted it as a BACKGROUND reload of a stale match: no pending UI, the page you left frozen until the chunk
    // landed (measured: 222ms at a 300ms chunk, only on section roots WITH a loader — teams, employees, orgs).
    // Blocking makes a stale match pend, so the skeleton paints on the click frame; it costs nothing, since no
    // loader here ever awaits anything.
    defaultStaleReloadMode: 'blocking',
    // A suspense query throws during render, so without this an auth/test error on a streamed query crashes the whole
    // tree. Pass the request-scoped i18n: the boundary renders ABOVE AppShell's provider and must not localize
    // against a concurrent request's locale (React #418).
    defaultErrorComponent: (props) => <DefaultRouteError {...props} i18n={i18nInstance} />,
    Wrap: (props: { children: ReactNode }) => {
      return (
        <PersistQueryClientProvider client={queryClient} persistOptions={persistOptions}>
          {props.children}
        </PersistQueryClientProvider>
      )
    },
  })

  setupRouterSsrQueryIntegration({
    router,
    queryClient,
    wrapQueryClient: false,
  })

  // Google Analytics (client + prod only). Consent-gated: GA loads and tracks only after the visitor
  // opts in to statistics cookies, and reacts to later changes. See lib/analytics.ts + cookie-consent.ts.
  setupAnalytics(router)

  if (!isServer) {
    // A dynamic import that cannot be fetched fires `vite:preloadError`. Do NOT reload: hashed assets persist on the
    // CDN, so a failed import is almost always the network, and reloading into a dead network blanks the page.
    // Suppress TanStack's own automatic reload too, so the rejection reaches the route error boundary.
    window.addEventListener('vite:preloadError', (event) => {
      suppressTanstackChunkReload((event as Event & { payload?: unknown }).payload)
    })

    // A new build deployed mid-session keeps the client on stale code. The next navigation goes through the document
    // (the root beforeLoad, lib/app-version) — or a reload, if it crashes first on a payload the new build reshaped
    // (#88, handled in route-error). This just records the version every response advertises.
    installVersionCapture()

    // Breadcrumbs are in-memory and cost nothing until a crash; the session timeline POSTs batches, so it only runs
    // when the wide-events sink is enabled.
    const sessionTimelineOn = window.WIDE_EVENTS_ENABLED === 'true'
    router.subscribe('onResolved', (event) => {
      addBreadcrumb('nav', event.toLocation.href)
      if (sessionTimelineOn) trackNavigation(event.toLocation.href)
    })
    installBreadcrumbCapture()
    if (sessionTimelineOn) installClientActionCapture()

    // Errors that escape React's error boundaries — async callbacks, event handlers, setTimeout, etc.
    // — never reach `defaultErrorComponent`, so report them here too.
    window.addEventListener('error', (event) => {
      // Cross-origin script errors (GA, third-party embeds) surface with `event.error == null` and no
      // actionable detail — skip them.
      if (event.error == null) return
      logFrontendError(event.error, 'global')
    })

    window.addEventListener('unhandledrejection', (event) => {
      logFrontendError(event.reason, 'unhandledrejection')
    })
  }

  return router
}

declare module '@tanstack/react-router' {
  interface Register {
    router: Awaited<ReturnType<typeof getRouter>>
  }
}
